import { Particle, firestoreTimestampNow } from '@newgenus/common';
import { Timestamp } from 'firebase/firestore';

/** Basket labels a particle leaves behind when it is filed. */
const BASKET_LABELS = ['INBASKET', 'PENDING'];

/**
 * The update payload that files a particle: its basket label swapped for DONE,
 * a filedDate stamp, and — for checklist items — the isComplete/dateCompleted
 * pair.
 *
 * Shared by ParticleService.fileParticleViaStore (the basket lists) and the
 * reading pane, which used to spell the same rules out by hand (C6). They still
 * dispatch it differently: the lists also correct the workspace basket counts,
 * which the pane has no access to.
 *
 * Pure — the particle handed in comes from the frozen NgRx store, so nothing
 * here may mutate it.
 */
export function buildFilePayload(particle: Particle, completeStatus = false): Record<string, any> {
    const labelIds = [...new Set((particle.labelIds || []).filter((l) => !BASKET_LABELS.includes(l)).concat('DONE'))];

    // Whole seconds: filedDate is compared and sorted against other stamps that
    // are written at second precision (the basket path zeroed the nanoseconds
    // after the fact; building it that way is the same value, minus the cast).
    const filedDate = new Timestamp(Timestamp.fromDate(new Date()).seconds, 0);

    const payload: Record<string, any> = { id: particle.particleKey, labelIds, filedDate };

    if (particle.particleType === 'checklist') {
        // Filing must never UNDO a prior "Done" — keep an existing complete
        // state unless this call itself completes it. dateCompleted (the
        // canonical field — `completedDate` was a junk write) is only set when
        // it actually changes, so a copy never echoes a mangled Timestamp back.
        const complete = completeStatus || !!particle.isComplete;
        payload.isComplete = complete;
        payload.alerting = false;

        if (complete && !particle.dateCompleted) payload.dateCompleted = firestoreTimestampNow();
        else if (!complete) payload.dateCompleted = null;
    }

    return payload;
}

/** The workspace doc's two live basket tallies, as the sidebar renders them. */
export interface BasketCounts {
    inbasketCount: number;
    pendingCount: number;
}

/**
 * The basket counts after a particle is filed: it leaves exactly one basket, so
 * exactly one tally drops by one (and neither moves for something already
 * filed).
 *
 * This is an OPTIMISTIC correction — the backend trigger recomputes the real
 * value moments later and the store subscription overwrites it. Every filing
 * path has to apply it, or its basket silently keeps counting a mail it no
 * longer holds (OE-90).
 */
export function countsAfterFile(particle: Particle, counts: BasketCounts): BasketCounts {
    const labels = particle?.labelIds || [];

    if (labels.includes('INBASKET')) return { ...counts, inbasketCount: counts.inbasketCount - 1 };
    if (labels.includes('PENDING')) return { ...counts, pendingCount: counts.pendingCount - 1 };

    return { ...counts };
}

/**
 * The basket a particle is being filed out of, as a route segment. The reading
 * pane navigates back to it once the file lands.
 */
export function basketBeingLeft(particle: Particle): string {
    return (particle?.labelIds || []).includes('INBASKET')
        ? 'inbasket'
        : (particle?.labelIds || []).includes('PENDING')
          ? 'pending'
          : 'inbasket';
}
